import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Dispatch } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { FaceLivenessDetectorCore } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider, createTheme } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { setWasmPaths } from '@tensorflow/tfjs-backend-wasm';
import type { KYCFlowState, KYCAction } from '../KYCFlowController';
import { REJECTION_REASON } from '../../constants/rejectionMessages';
import { LIVENESS_SESSION_STATUS } from '../../types/kyc';
import { useLivenessSession, LIVENESS_HOOK_STATE } from '../../hooks/useLivenessSession';
import { LivenessGuide } from './liveness/LivenessGuide';

// Apuntar al SDK de Amplify/TF.js hacia los archivos WASM servidos por nginx.
// CRA no copia los .wasm de node_modules al build — se copian explícitamente a public/tfjs-wasm/.
// Debe ejecutarse a nivel de módulo, antes de que cualquier componente monte FaceLivenessDetectorCore.
setWasmPaths('/tfjs-wasm/');

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

// Región AWS donde están configurados los recursos de Rekognition Face Liveness
const AWS_REGION = process.env.REACT_APP_AWS_REGION ?? 'us-east-1';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AMPLIFY_THEME: any = (createTheme as any)({});

// Traducción completa al español de los textos del SDK de Amplify Face Liveness.
// El SDK muestra estos textos durante la captura (hints en tiempo real, mensajes
// de cámara, errores y botón de cancelar). Sin este prop, todo está en inglés.
const LIVENESS_DISPLAY_TEXT = {
  // Instrucciones en tiempo real durante la captura
  hintMoveFaceFrontOfCameraText:  'Coloca tu rostro frente a la cámara',
  hintTooManyFacesText:           'Asegúrate de que solo haya un rostro en cámara',
  hintFaceDetectedText:           'Rostro detectado',
  hintCanNotIdentifyText:         'No podemos identificar tu rostro',
  hintTooCloseText:               'Estás demasiado cerca',
  hintTooFarText:                 'Acércate un poco más',
  hintConnectingText:             'Conectando…',
  hintVerifyingText:              'Verificando…',
  hintCheckCompleteText:          'Verificación completada',
  hintIlluminationTooBrightText:  'Demasiada luz — busca un lugar con menos brillo',
  hintIlluminationTooDarkText:    'Poca iluminación — busca más luz',
  hintIlluminationNormalText:     'Iluminación correcta',
  hintHoldFaceForFreshnessText:   'Mantén tu rostro quieto',
  hintCenterFaceText:             'Centra tu rostro',
  hintCenterFaceInstructionText:  'Centra tu rostro en el óvalo',
  hintFaceOffCenterText:          'Mueve tu rostro al centro',
  hintMatchIndicatorText:         'Mantén tu rostro dentro del óvalo',
  // Mensajes de cámara y permisos
  cameraMinSpecificationsHeadingText: 'La cámara no cumple los requisitos mínimos',
  cameraMinSpecificationsMessageText: 'Tu cámara no tiene la resolución o velocidad de fotogramas requerida.',
  cameraNotFoundHeadingText:          'Cámara no encontrada',
  cameraNotFoundMessageText:          'Activa los permisos de cámara en tu navegador y vuelve a intentarlo.',
  retryCameraPermissionsText:         'Verificar permisos de cámara',
  waitingCameraPermissionText:        'Esperando permiso de cámara…',
  a11yVideoLabelText:                 'Video de cámara para verificación facial',
  // Indicadores de grabación y botón de cancelar
  recordingIndicatorText:   'Grabando',
  cancelLivenessCheckText:  'Cancelar',
  // Mensajes de error internos del SDK
  errorLabelText:                   'Error',
  connectionTimeoutHeaderText:      'Tiempo de conexión agotado',
  connectionTimeoutMessageText:     'La conexión tardó demasiado. Verifica tu internet y vuelve a intentarlo.',
  timeoutHeaderText:                'Tiempo de verificación agotado',
  timeoutMessageText:               'La verificación tardó demasiado. Asegúrate de estar bien iluminado y vuelve a intentarlo.',
  faceDistanceHeaderText:           'Distancia incorrecta',
  faceDistanceMessageText:          'Mantén el rostro a la distancia adecuada de la cámara durante la verificación.',
  multipleFacesHeaderText:          'Más de un rostro detectado',
  multipleFacesMessageText:         'Asegúrate de estar solo frente a la cámara.',
  clientHeaderText:                 'Error en la verificación',
  clientMessageText:                'Ocurrió un error en tu dispositivo. Vuelve a intentarlo.',
  serverHeaderText:                 'Error del servidor',
  serverMessageText:                'Ocurrió un error en el servidor. Intenta de nuevo más tarde.',
  landscapeHeaderText:              'Orientación horizontal no permitida',
  landscapeMessageText:             'Rota tu dispositivo a posición vertical para continuar.',
  portraitMessageText:              'La verificación requiere que el dispositivo esté en posición vertical.',
  tryAgainText:                     'Volver a intentar',
};

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface LivenessStepProps {
  state: KYCFlowState;
  dispatch: Dispatch<KYCAction>;
}

// Fase interna del componente — controla qué sub-vista renderizar
type LivenessPhase = 'guide' | 'session';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-vistas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pantalla de espera mientras se crea la sesión en el liveness-service.
 * Se muestra durante la transición guide → ready (estado 'creating').
 */
function CreatingSessionView() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 6,
      }}
    >
      <CircularProgress size={40} />
      <Typography variant="body1" color="text.secondary">
        Preparando la prueba de vida…
      </Typography>
    </Box>
  );
}

/**
 * Overlay de espera que se superpone al flujo una vez que el SDK de Amplify
 * terminó de transmitir el video y el backend está calculando el veredicto.
 */
function PollingView() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 6,
      }}
    >
      <CircularProgress size={40} />
      <Typography variant="body1" color="text.secondary">
        Procesando resultado…
      </Typography>
      <Typography variant="caption" color="text.disabled">
        Esto puede tardar unos segundos.
      </Typography>
    </Box>
  );
}

/**
 * Pantalla de error técnico con opción de reintento.
 * Se muestra cuando livenessState === ERROR (red, servicio no disponible, sesión expirada).
 */
interface ErrorViewProps {
  message: string;
  onRetry: () => void;
}

function ErrorView({ message, onRetry }: ErrorViewProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="error">{message}</Alert>
      <Button
        variant="outlined"
        startIcon={<ReplayIcon />}
        onClick={onRetry}
        fullWidth
      >
        Intentar de nuevo
      </Button>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Paso de Prueba de Vida dentro del flujo KYC.
 *
 * Orquesta dos fases:
 *   1. guide  — LivenessGuide: instrucciones + preview de cámara + detección de rostro
 *   2. session — FaceLivenessDetector (Amplify SDK): captura y transmisión a KVS
 *
 * Máquina de estados interna (livenessState de useLivenessSession):
 *   idle      → guide renderiza LivenessGuide
 *   creating  → muestra spinner de "Preparando…"
 *   ready     → FaceLivenessDetector activo con sessionId
 *   polling   → overlay "Procesando resultado…"
 *   succeeded → useEffect despacha LIVENESS_SUCCESS o LIVENESS_FAILED al reducer KYC
 *   error     → ErrorView con botón de reintento
 *
 * El componente es responsable de traducir el resultado del liveness-service
 * (is_live: boolean | null) en las acciones del reducer KYC del padre.
 */
export function LivenessStep({ state, dispatch }: LivenessStepProps) {
  // Fase interna: 'guide' mientras el usuario se prepara; 'session' durante la captura
  const [phase, setPhase] = useState<LivenessPhase>('guide');

  // Error proveniente del SDK de Amplify (cámara, red, sesión inválida)
  // Se maneja separado del errorMessage del hook para no mezclar fuentes de error
  const [sdkError, setSdkError] = useState<string | null>(null);

  // deviceId de la cámara frontal, detectado al confirmar la guía (permiso ya otorgado).
  // Se pasa al SDK vía config.deviceId para que Android no seleccione la trasera.
  const [frontCameraDeviceId, setFrontCameraDeviceId] = useState<string | undefined>(undefined);

  const {
    livenessState,
    livenessSessionId,
    result,
    errorMessage,
    credentialProvider,
    createSession,
    handleAnalysisComplete,
    reset,
  } = useLivenessSession({
    userId: state.userId ?? undefined,
    // El filename del documento se pasa al liveness-service para la comparación facial.
    // Rekognition CompareFaces verifica que quien realiza la prueba sea el titular del documento.
    docFilename: state.finalDocFilename ?? undefined,
  });

  // Cuando el resultado está disponible, traducirlo a acciones del reducer KYC.
  // Orden de evaluación:
  //   1. Si la sesión no fue SUCCEEDED o el sujeto no está vivo → LIVENESS_FAILED
  //   2. Si la comparación facial retornó false explícito → FACE_MISMATCH (no reintentable)
  //   3. Si face_match es null (comparación no ejecutada o falló técnicamente) → continuar
  //   4. Si todo pasó → LIVENESS_SUCCESS
  useEffect(() => {
    if (livenessState !== LIVENESS_HOOK_STATE.SUCCEEDED || result === null) {
      return;
    }

    if (result.status === LIVENESS_SESSION_STATUS.SUCCEEDED && result.isLive === true) {
      // La persona está viva. Evaluar resultado de comparación facial.
      if (result.faceMatch === false) {
        // Persona distinta al documento — rechazo definitivo, no reintentable.
        dispatch({
          type: 'LIVENESS_FAILED',
          reason: REJECTION_REASON.FACE_MISMATCH,
        });
        return;
      }
      if (result.faceMatch === null) {
        // Error técnico en comparación facial — solicitar repetir la prueba de vida.
        dispatch({
          type: 'LIVENESS_FAILED',
          reason: REJECTION_REASON.FACE_COMPARISON_FAILED,
        });
        return;
      }
      // face_match === true — persona verificada correctamente.
      dispatch({
        type: 'LIVENESS_SUCCESS',
        score: result.confidence ?? 0,
      });
    } else {
      dispatch({
        type: 'LIVENESS_FAILED',
        reason: REJECTION_REASON.LIVENESS_FAILED,
      });
    }
  }, [livenessState, result, dispatch]);

  // Llamado por LivenessGuide cuando el usuario confirma que está listo.
  // Enumera los dispositivos de video ANTES de cambiar de fase — el permiso ya fue
  // otorgado en LivenessGuide, por lo que las etiquetas de dispositivo están disponibles.
  // Identificar el deviceId de la cámara frontal permite pasarlo al SDK vía config.deviceId
  // y evitar que Android seleccione la cámara trasera por defecto.
  const handleGuideReady = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      const frontDevice =
        videoDevices.find((d) => {
          const label = d.label.toLowerCase();
          return (
            label.includes('front') ||
            label.includes('frontal') ||
            label.includes('user') ||
            label.includes('facing front')
          );
        }) ?? videoDevices[0];
      if (frontDevice?.deviceId) {
        setFrontCameraDeviceId(frontDevice.deviceId);
      }
    } catch {
      // Si la enumeración falla, continuar sin deviceId — el SDK seleccionará la cámara por defecto
    }
    setPhase('session');
    void createSession();
  }, [createSession]);

  // Resetea toda la lógica de liveness y regresa a la guía de instrucciones
  const handleRetry = useCallback(() => {
    setSdkError(null);
    reset();
    setPhase('guide');
  }, [reset]);

  // ── Fase: guía de instrucciones ──────────────────────────────────────────
  if (phase === 'guide') {
    return (
      <LivenessGuide
        onReady={handleGuideReady}
        attemptNumber={state.livenessRetries}
        skipFaceDetection={state.livenessRetries > 0}
      />
    );
  }

  // ── Fase: sesión de liveness ─────────────────────────────────────────────

  // Creando sesión — spinner transitorio antes de tener session_id
  if (livenessState === LIVENESS_HOOK_STATE.CREATING) {
    return <CreatingSessionView />;
  }

  // Error técnico — puede provenir del hook (red, sesión) o del SDK de Amplify (cámara)
  if (livenessState === LIVENESS_HOOK_STATE.ERROR || sdkError !== null) {
    return (
      <ErrorView
        message={sdkError ?? errorMessage ?? 'Ocurrió un error inesperado. Intenta de nuevo.'}
        onRetry={handleRetry}
      />
    );
  }

  // Procesando resultado — SDK terminó de transmitir, esperando veredicto
  if (livenessState === LIVENESS_HOOK_STATE.POLLING) {
    return <PollingView />;
  }

  // Resultado disponible — el useEffect de arriba ya despachó la acción al reducer
  // Mostrar spinner breve mientras el reducer hace la transición de estado
  if (livenessState === LIVENESS_HOOK_STATE.SUCCEEDED) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Estado 'ready' — session_id disponible, renderizar el SDK de Amplify
  // También cubre el estado 'idle' improbable (no debería ocurrir en fase 'session')
  if (livenessState !== LIVENESS_HOOK_STATE.READY || !livenessSessionId) {
    return <CreatingSessionView />;
  }

  return (
    <ThemeProvider theme={AMPLIFY_THEME}>
      <LivenessDetectorWrapper
        sessionId={livenessSessionId}
        region={AWS_REGION}
        onAnalysisComplete={handleAnalysisComplete}
        onError={(errorState: { state: string; error?: Error }) => {
          // El SDK de Amplify puede emitir errores de cámara, red o sesión expirada.
          // Se captura en estado local para mostrar ErrorView con mensaje descriptivo.
          const message =
            errorState.state === 'CAMERA_ACCESS_ERROR'
              ? 'No se pudo acceder a la cámara. Verifica los permisos e intenta de nuevo.'
              : errorState.state === 'SERVER_ERROR'
              ? 'Error al conectarse con el servidor de prueba de vida. Intenta de nuevo.'
              : 'Error durante la prueba de vida. Intenta de nuevo.';

          setSdkError(message);
        }}
        config={{ credentialProvider, deviceId: frontCameraDeviceId }}
        disableStartScreen={true}
        displayText={LIVENESS_DISPLAY_TEXT}
      />
    </ThemeProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper de limpieza de cámara
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Envuelve FaceLivenessDetectorCore y garantiza que el stream de cámara se libere
 * al desmontar el componente.
 *
 * El SDK de Amplify obtiene el stream con getUserMedia pero no llama a track.stop()
 * al desmontar, dejando el LED de cámara encendido. Se parchea getUserMedia para
 * capturar la referencia al stream; en el cleanup se detienen todos los tracks.
 *
 * La selección de cámara (frontal vs trasera) se delega a config.deviceId, que
 * se resuelve en LivenessStep antes de montar este componente.
 */
type LivenessDetectorProps = React.ComponentProps<typeof FaceLivenessDetectorCore>;

function LivenessDetectorWrapper(props: LivenessDetectorProps) {
  const capturedStreamsRef = useRef<MediaStream[]>([]);

  // Parchea getUserMedia únicamente para capturar la referencia al stream y poder
  // detener los tracks al desmontar. No modifica los constraints — la selección de
  // cámara ya fue resuelta por config.deviceId antes de que este componente monte.
  useLayoutEffect(() => {
    const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

    navigator.mediaDevices.getUserMedia = async function (
      constraints: MediaStreamConstraints,
    ): Promise<MediaStream> {
      const stream = await original(constraints);
      capturedStreamsRef.current.push(stream);
      return stream;
    };

    return () => {
      navigator.mediaDevices.getUserMedia = original;
      capturedStreamsRef.current.forEach((stream) => {
        stream.getTracks().forEach((track) => track.stop());
      });
      capturedStreamsRef.current = [];
    };
  }, []);

  return <FaceLivenessDetectorCore {...props} />;
}
